import {TargetBlank} from '@wandb/weave/common/util/links';
import {Switch} from '@wandb/weave/components';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import Color from 'color';
import * as React from 'react';
// eslint-disable-next-line wandb/no-deprecated-imports
import {
  Accordion,
  Button,
  Dropdown,
  Icon,
  Input,
  TextArea,
} from 'semantic-ui-react';

import {getTextColor} from '../util/colors';
import DOC_URLS from '../util/doc_urls';
import {getLegendOverrideKey} from '../util/plotHelpers/legend';
import {Mark, RunDataSeries} from '../util/plotHelpers/types';
import {Query} from '../util/queryTypes';
import ColorPicker from './ColorPicker';
import {LabeledOptionBlock as LabeledOption} from './elements/LabeledOptionBlock';
import LineStylePicker from './LineStylePicker';
import {useSingleRunModeContext} from './MultiRunWorkspace/SingleRunMode';
import PanelChartOptions from './PanelChartOptions';
import {usePanelConfigContext} from './PanelRunsLinePlot/PanelConfigContext';
import {RunsLinePlotConfig} from './PanelRunsLinePlot/types';
import {useColorByMetrics} from './PanelRunsLinePlot/useColorByMetrics';
import {useLegendVisibility} from './PanelRunsLinePlot/useLegendVisibility';
import ProjectFieldSelector from './ProjectFieldSelector';

type PanelConfig = {
  metrics?: string[];
  legendFields?: string[];
  legendTemplate?: string;
  colorEachMetricDifferently?: boolean;
  overrideSeriesTitles?: {[key: string]: string};
  overrideColors?: {[key: string]: {color: string; transparentColor: string}};
  overrideMarks?: {[key: string]: Mark};
};

type PanelDisplayPreferencesProps = {
  type: 'lines' | 'bars';
  config: PanelConfig;
  updateConfig: (newConfig: PanelConfig) => void;
  defaultTitle?: string;
  defaultXAxisTitle?: string;
  defaultYAxisTitle?: string;
  pageQuery: Query; // for the field selector
  defaultLegendTemplate?: string;
  editableLegendSeries: RunDataSeries[];
};

const PanelDisplayPreferences: React.FC<PanelDisplayPreferencesProps> =
  React.memo(
    ({
      pageQuery,
      defaultLegendTemplate,
      type,
      editableLegendSeries,
      config,
      updateConfig,
    }) => {
      const [advancedLegendVisible, setAdvancedLegendVisible] =
        React.useState(false);
      const {isMultiMetric, showLegend} = usePanelConfigContext();
      const isLegendShown = useLegendVisibility(showLegend, isMultiMetric);
      const colorEachMetricDifferently = useColorByMetrics(config);
      return (
        <>
          <PanelChartOptions
            config={config}
            pageQuery={pageQuery}
            showLegend={isLegendShown}
            type={'lines'}
            updateConfig={updateConfig}
          />
          <LabeledOption
            label="Legend"
            labelWidth="140px"
            helpText="Set variables in the chart legend"
            docUrl={DOC_URLS.compareMetrics + '#legend'}
            option={
              <ProjectFieldSelector
                className="legend"
                disabled={!config.metrics || config.metrics.length === 0}
                query={pageQuery}
                types={['string', 'number', 'boolean']}
                defaultKeys={[
                  'run:displayName',
                  'run:name',
                  'run:createdAt',
                  'run:userName',
                ]}
                selection
                fluid
                multi
                value={config.legendFields || ['run:displayName']}
                searchByKeyAndText
                setValue={value =>
                  updateConfig({legendFields: value, legendTemplate: undefined})
                }
              />
            }
          />
          {isMultiMetric && (
            <LabeledOption
              label="Color metrics"
              helpText={`Color ${type} by metrics instead of runs`}
              labelWidth="140px"
              option={
                <Tailwind>
                  <Switch.Root
                    size="medium"
                    checked={colorEachMetricDifferently}
                    onCheckedChange={isChecked =>
                      updateConfig({colorEachMetricDifferently: isChecked})
                    }>
                    <Switch.Thumb
                      size="medium"
                      checked={colorEachMetricDifferently}
                    />
                  </Switch.Root>
                </Tailwind>
              }
            />
          )}
          <Accordion className="advanced-options">
            <Accordion.Title
              active={advancedLegendVisible}
              onClick={() => {
                setAdvancedLegendVisible(cur => !cur);
              }}>
              <Icon name="dropdown" />
              Advanced Legend
            </Accordion.Title>
            <Accordion.Content active={advancedLegendVisible}>
              <p className="advanced-legend-info chart-label">
                {`Edit the legend template directly. For example:`}
                <br />
                {`\${config:epochs} displays the config value epochs for each line.`}
                <br />
                {`\${summary:loss} displays the summary value loss for each line.`}
                <br />
                {`[[ $x: $y ]] to displays history values in the tooltip hovering over a line.`}
                <br />

                <TargetBlank href={DOC_URLS.compareMetrics + '#legend'}>
                  See the docs →
                </TargetBlank>
              </p>
              <TextArea
                value={config.legendTemplate ?? defaultLegendTemplate}
                className="chart-legend-template"
                onChange={(e, {value}) => {
                  updateConfig({legendTemplate: value?.toString()});
                }}
              />
            </Accordion.Content>
          </Accordion>

          <OverrideDataSeriesConfig
            editColor
            editMark={type === 'lines'}
            editLineWidths={type === 'lines'}
            config={config}
            updateConfig={updateConfig}
            editableLegendSeries={editableLegendSeries}
          />
        </>
      );
    }
  );

export default PanelDisplayPreferences;

function getLegendDisplayName(line: RunDataSeries): string {
  if (!line.displayName) {
    return '';
  }
  return `${line.displayName} ${line.metricName ?? ''}`;
}

type OverrideDataSeriesConfigProps = {
  config: RunsLinePlotConfig;
  updateConfig: (newConfig: RunsLinePlotConfig) => void;
  editableLegendSeries: RunDataSeries[];
  editColor: boolean;
  editMark: boolean;
  editLineWidths: boolean;
};
const OverrideDataSeriesConfig: React.FC<OverrideDataSeriesConfigProps> =
  React.memo(
    ({
      config,
      updateConfig,
      editColor,
      editMark,
      editLineWidths,
      editableLegendSeries,
    }) => {
      const {isSingleRun} = useSingleRunModeContext();

      if (editableLegendSeries.length === 0) {
        return <p>No editable lines on this chart.</p>;
      }
      const thicknessOptions = [0.5, 1, 1.5, 2, 3].map(val => ({
        key: val,
        text: `${val}px`,
        value: val,
      }));

      return (
        <div className="legend-options">
          {editableLegendSeries.map((l, i) => {
            const lineKey = getLegendOverrideKey(l, !isSingleRun);
            const lineKeyDisplayName = getLegendDisplayName(l);
            const usingOverride =
              config.overrideSeriesTitles?.[lineKey] != null ||
              config.overrideColors?.[lineKey] != null ||
              config.overrideMarks?.[lineKey] != null;

            const runColor = config.overrideColors?.[lineKey]?.color ?? l.color;

            return (
              <div key={i} className="legend-option">
                <div className="series-name">
                  <p
                    className="series-name-value"
                    style={{
                      color: getTextColor(runColor),
                    }}>
                    {lineKeyDisplayName}
                  </p>
                </div>
                <div className="override-name">
                  {editColor && (
                    <ColorPicker
                      activeColor={runColor || '#999999'}
                      // With current optimizations, some components do not update on function prop changes.
                      // To avoid a stale callback, we include an empty style prop to force a re-render.
                      // (This is a workaround, not the ideal solution.)
                      // Refer to shouldUpdate.ts : L16
                      style={{}}
                      setColor={value => {
                        const newOverrideColors = {...config.overrideColors};
                        const colors = {
                          color: value,
                          transparentColor: Color(value).alpha(0.1).toString(),
                        };

                        newOverrideColors[lineKey] = colors;
                        updateConfig({
                          overrideColors: newOverrideColors,
                        });
                      }}
                    />
                  )}
                  {editMark && (
                    <LineStylePicker
                      activeLineStyle={
                        config.overrideMarks?.[lineKey] ||
                        editableLegendSeries[i].mark ||
                        'solid'
                      }
                      setLineStyle={(mark: Mark) => {
                        const newOverrideMarks = {...config.overrideMarks};
                        newOverrideMarks[lineKey] = mark;
                        updateConfig({
                          overrideMarks: newOverrideMarks,
                        });
                      }}
                    />
                  )}
                  {editLineWidths && (
                    <Dropdown
                      className="line-width-picker"
                      defaultValue={config.overrideLineWidths?.[lineKey] || 1}
                      selection
                      options={thicknessOptions}
                      onChange={(e, {value}) => {
                        const newOverrideLineWidths = {
                          ...config.overrideLineWidths,
                        };

                        newOverrideLineWidths[lineKey] = Number.parseFloat(
                          (value ?? 1).toString()
                        );
                        updateConfig({
                          overrideLineWidths: newOverrideLineWidths,
                        });
                      }}
                    />
                  )}
                  <Input
                    className="override-value"
                    defaultValue={l.title?.toString()}
                    onChange={(e, {value}) => {
                      const newOverrideLineTitles = {
                        ...config.overrideSeriesTitles,
                      };

                      newOverrideLineTitles[lineKey] = value;
                      updateConfig({
                        overrideSeriesTitles: newOverrideLineTitles,
                      });
                    }}
                  />
                  <Button
                    icon
                    disabled={!usingOverride}
                    onClick={(e, {value}) => {
                      const newOverrideSeriesTitles = {
                        ...config.overrideSeriesTitles,
                      };
                      delete newOverrideSeriesTitles[lineKey];

                      const newOverrideColors = {...config.overrideColors};
                      delete newOverrideColors[lineKey];

                      const newOverrideMarks = {...config.overrideMarks};
                      delete newOverrideMarks[lineKey];

                      updateConfig({
                        overrideSeriesTitles: newOverrideSeriesTitles,
                        overrideColors: newOverrideColors,
                        overrideMarks: newOverrideMarks,
                      });
                    }}>
                    <Icon name="sync" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      );
    }
  );
